import React, { memo, Fragment, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import map from 'lodash/map';
import { Skeleton } from 'antd';
import {
  allFiltersSelector,
  unitModeSelector,
  liveModeSelector,
} from '@store/metricLens/base/selectors';
import {
  loadingSelector,
  historicalRangeMaxMetricsDataSelector,
  historicalRangeTotalDataSelector,
} from '@store/metricLens/historicalRangeData/selectors';
import {
  liveLoadSelector,
  liveRangeTotalDataSelector,
  liveMaxMetricsDataSelector,
} from '@store/metricLens/liveData/selectors';
import { shouldCalcPercentage } from '@utils/metrics';
import EachCell from './Cell';
import BarChart from '../../Chart/BarChart/BarChart';
import {
  StyledCellWrapper,
  StyledCellContainer,
  StyledChartContainer,
  StyledSkeleton,
} from '../styled';

const Range = (props) => {
  const { cellData, unit, column, rowData } = props;

  const allFilters = useSelector(allFiltersSelector);
  const liveMode = useSelector(liveModeSelector);
  const liveValues = {
    maxMetrics: useSelector(liveMaxMetricsDataSelector),
    load: useSelector(liveLoadSelector),
    range: useSelector(liveRangeTotalDataSelector),
  };
  const historyValues = {
    maxMetrics: useSelector(historicalRangeMaxMetricsDataSelector),
    load: useSelector(loadingSelector),
    range: useSelector(historicalRangeTotalDataSelector),
  };

  let maxMetricValues = historyValues.maxMetrics;
  let loadData = historyValues.load;
  let totalMetricValues = historyValues.range;
  if (liveMode) {
    maxMetricValues = liveValues.maxMetrics;
    loadData = liveValues.load;
    totalMetricValues = liveValues.range;
  }

  const unitMode = useSelector(unitModeSelector);
  const [compiledCellData, setCompiledCellData] = useState(cellData);
  const [compiledUnit, setCompiledUnit] = useState(unit);
  const appliedColors = map(allFilters, 'color');
  const filters = map(allFilters, 'filter.name');
  const shouldCalculatePercentage = shouldCalcPercentage({ metricKey: column.dataKey });

  useEffect(() => {
    if (unitMode === '%' && shouldCalculatePercentage) {
      const percentageMetrics = [];
      cellData.forEach((eachData, index) => {
        let calculatedValue = (eachData / totalMetricValues[index][column.dataKey]) * 100;
        if (isNaN(calculatedValue)) {
          calculatedValue = 'NA';
        }
        percentageMetrics.push(calculatedValue);
      });
      setCompiledCellData(percentageMetrics);
      setCompiledUnit('%');
    } else {
      setCompiledCellData(cellData);
      setCompiledUnit(unit);
    }
  }, [cellData, unitMode, column, totalMetricValues, unit, shouldCalculatePercentage]);

  return (
    <StyledCellWrapper>
      {loadData.loading ? (
        <StyledSkeleton>
          <Skeleton
            active
            paragraph={{
              rows: allFilters.length,
              width: '100%',
            }}
          />
        </StyledSkeleton>
      ) : (
        <Fragment>
          <StyledCellContainer>
            {compiledCellData && compiledCellData.length > 0
              ? compiledCellData.map((cell, index) => (
                  <EachCell key={`${cell} ${index}`} cellData={cell} unit={compiledUnit} />
                ))
              : null}
          </StyledCellContainer>
          <StyledChartContainer>
            <BarChart
              cellData={compiledCellData}
              unit={compiledUnit}
              shouldCalculatePercentage={shouldCalculatePercentage}
              appliedColors={appliedColors}
              maxMetricValue={maxMetricValues[column.dataKey]}
              dimensionName={rowData.name[0]}
              filters={filters}
            />
          </StyledChartContainer>
        </Fragment>
      )}
    </StyledCellWrapper>
  );
};

export default memo(Range);
